import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "../lib/api";

function b64ToArrayBuffer(b64) {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

function newId() {
  return (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2));
}

// Stable identity (long-term profile memory) + per-conversation session (history).
function getUserId() {
  let id = localStorage.getItem("luka_uid");
  if (!id) { id = newId(); localStorage.setItem("luka_uid", id); }
  return id;
}
function getSessionId() {
  let id = localStorage.getItem("luka_sid");
  if (!id) { id = newId(); localStorage.setItem("luka_sid", id); }
  return id;
}

export function useVoiceChat() {
  const [connected] = useState(true);
  const [thinking, setThinking] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [messages, setMessages] = useState([]);
  const [spokenWords, setSpokenWords] = useState(0);

  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const queueRef = useRef([]);
  const playingRef = useRef(false);
  const assistantOpenRef = useRef(false);
  const messagesRef = useRef([]);
  const turnRef = useRef(0);
  const wordBaseRef = useRef(0);
  const highlightTimerRef = useRef(null);

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const stopHighlight = useCallback(() => {
    if (highlightTimerRef.current) {
      clearInterval(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
  }, []);

  // Reveal the words of the current segment in time with its audio (karaoke).
  const animateHighlight = useCallback((base, count, durSec) => {
    stopHighlight();
    setSpokenWords(base);
    if (count <= 0) return;
    let i = 0;
    const stepMs = Math.max((durSec * 1000) / count, 110);
    highlightTimerRef.current = setInterval(() => {
      i += 1;
      setSpokenWords(base + i);
      if (i >= count) stopHighlight();
    }, stepMs);
  }, [stopHighlight]);

  const ensureAudio = useCallback(() => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.7;
      analyser.connect(ctx.destination);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
    }
    if (audioCtxRef.current.state === "suspended") audioCtxRef.current.resume();
    return audioCtxRef.current;
  }, []);

  const appendAssistant = useCallback((text) => {
    setMessages((prev) => {
      if (!assistantOpenRef.current) {
        assistantOpenRef.current = true;
        return [...prev, { role: "assistant", text }];
      }
      const copy = [...prev];
      const last = copy[copy.length - 1];
      if (last && last.role === "assistant") {
        copy[copy.length - 1] = { ...last, text: (last.text + " " + text).trim() };
      } else {
        copy.push({ role: "assistant", text });
      }
      return copy;
    });
  }, []);

  const playNext = useCallback(async () => {
    if (playingRef.current) return;
    const item = queueRef.current.shift();
    if (!item) {
      setSpeaking(false);
      sourceRef.current = null;
      stopHighlight();
      setSpokenWords(wordBaseRef.current); // everything spoken
      return;
    }
    playingRef.current = true;
    setSpeaking(true);

    const segWordCount = (item.text || "").trim().split(/\s+/).filter(Boolean).length;
    const base = wordBaseRef.current;
    appendAssistant(item.text); // grow transcript in sync with speech
    wordBaseRef.current = base + segWordCount;

    const ctx = ensureAudio();
    const finish = () => {
      playingRef.current = false;
      sourceRef.current = null;
      setSpokenWords(base + segWordCount);
      playNext();
    };
    if (!item.audio) {
      animateHighlight(base, segWordCount, 0.7);
      setTimeout(finish, 700);
      return;
    }
    try {
      const buf = await ctx.decodeAudioData(b64ToArrayBuffer(item.audio));
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(analyserRef.current);
      src.onended = finish;
      sourceRef.current = src;
      src.start(0);
      animateHighlight(base, segWordCount, buf.duration || 0.7);
    } catch (e) {
      animateHighlight(base, segWordCount, 0.6);
      setTimeout(finish, 600);
    }
  }, [ensureAudio, appendAssistant, animateHighlight, stopHighlight]);

  const hardStop = useCallback(() => {
    queueRef.current = [];
    if (sourceRef.current) {
      try { sourceRef.current.onended = null; sourceRef.current.stop(); } catch (e) {}
      sourceRef.current = null;
    }
    playingRef.current = false;
    stopHighlight();
  }, [stopHighlight]);

  const sendMessage = useCallback(
    (text, agentId) => {
      ensureAudio();
      const myTurn = ++turnRef.current;
      assistantOpenRef.current = false;
      wordBaseRef.current = 0;
      setSpokenWords(0);
      hardStop();
      const prior = messagesRef.current.slice(-6).map((m) => ({ role: m.role, text: m.text }));
      setMessages((prev) => [...prev, { role: "user", text }]);
      setThinking(true);

      (async () => {
        try {
          const res = await fetch(`${API}/voice/chat`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              agent_id: agentId,
              text,
              history: prior,
              session_id: getSessionId(),
              user_id: getUserId(),
            }),
          });
          if (!res.ok || !res.body) throw new Error("http " + res.status);

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let got = false;
          let errored = false;

          const handleLine = (line) => {
            const trimmed = line.trim();
            if (!trimmed) return;
            let obj;
            try { obj = JSON.parse(trimmed); } catch (e) { return; }
            if (myTurn !== turnRef.current) return; // superseded
            if (obj.error) { errored = true; return; }
            if (!obj.text && !obj.audio) return;
            if (!got) { got = true; setThinking(false); }
            queueRef.current.push({ text: obj.text, audio: obj.audio });
            playNext();
          };

          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            if (myTurn !== turnRef.current) { try { reader.cancel(); } catch (e) {} return; }
            buffer += decoder.decode(value, { stream: true });
            let nl;
            while ((nl = buffer.indexOf("\n")) >= 0) {
              const line = buffer.slice(0, nl);
              buffer = buffer.slice(nl + 1);
              handleLine(line);
            }
          }
          if (buffer) handleLine(buffer);

          if (myTurn !== turnRef.current) return;
          if (errored) {
            setThinking(false);
            setMessages((prev) => [...prev, { role: "system", text: "Ошибка ответа. Попробуйте ещё раз." }]);
          } else if (!got) {
            setThinking(false);
            setMessages((prev) => [...prev, { role: "system", text: "No reply right now." }]);
          }
        } catch (e) {
          if (myTurn !== turnRef.current) return;
          setThinking(false);
          setMessages((prev) => [...prev, { role: "system", text: "Ошибка ответа. Попробуйте ещё раз." }]);
        }
      })();

      return true;
    },
    [ensureAudio, hardStop, playNext]
  );

  const stopAudio = useCallback(() => {
    hardStop();
    setSpeaking(false);
    setThinking(false);
  }, [hardStop]);

  const clear = useCallback(() => {
    turnRef.current++; // drop any in-flight response
    hardStop();
    assistantOpenRef.current = false;
    wordBaseRef.current = 0;
    setSpokenWords(0);
    setSpeaking(false);
    setThinking(false);
    setMessages([]);
    localStorage.setItem("luka_sid", newId()); // fresh conversation; profile (luka_uid) stays
  }, [hardStop]);

  return { connected, thinking, speaking, spokenWords, messages, analyserRef, sendMessage, stopAudio, clear };
}
